/**
 * ECharts Cash Flow Option Builders
 *
 * Pure functions that build EChartsOption objects from computed curve data.
 * Keeps component files clean and options testable.
 */
import type { DataZoomComponentOption } from 'echarts/components'

import type {
  AwardPortfolioItem,
  IdvGroupTimeSeriesResponse,
  TimeSeriesPoint,
} from '@/types/usaspending'

import {
  type ChartMode,
  type CumulativePoint,
  OUTLAY_REPORTING_START,
  buildFyMarkLines,
  computeCumulativeObligation,
  computeMonthlyObligation,
  computeMonthlyOutlay,
  computePerAwardCumulative,
  formatCurrency,
  forwardFillCurve,
  safeNum,
} from './cashflow-utils'
import type { EChartsOption } from './echarts-setup'
import { type IdvGroup, getChildColor } from './idv-grouping'

// ============================================================================
// Module-level constants
// ============================================================================

const MONTH_ABBREVS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

// ============================================================================
// Shared helpers
// ============================================================================

/** Convert CumulativePoint[] to ECharts [timestamp, value] data */
function toTimeData(curve: CumulativePoint[]): [number, number][] {
  return curve.map((p) => [p.x.getTime(), p.y])
}

/**
 * Forward-fill lookup: given a sorted-ascending [timestamp, value] array,
 * returns the value of the most recent data point at or before the query time.
 * Returns null if the query is before the first data point.
 *
 * This is used so tooltip series values are always populated — with trigger:'axis'
 * ECharts only includes a series in params when the crosshair snaps to a timestamp
 * that exists in THAT series' data. Ceiling, obligation, and disbursement have
 * different sparse timestamps, so without this, some series are silently absent.
 */
function makeForwardFillLookup(
  data: [number, number][],
): (t: number) => number | null {
  return (t: number): number | null => {
    let result: number | null = null
    for (const [dt, val] of data) {
      if (dt <= t) result = val
      else break
    }
    return result
  }
}

/** Inline marker dot matching ECharts' default legend/tooltip icon style */
function markerDot(color: string): string {
  return `<span style="display:inline-block;margin-right:4px;border-radius:50%;width:8px;height:8px;background-color:${color}"></span>`
}

/**
 * Build a single-award tooltip formatter closed over the chart's series data.
 *
 * Uses forward-fill Maps for all series so the tooltip always shows obligation,
 * disbursement, and ceiling — even when the ECharts crosshair snaps to a timestamp
 * that only exists in one of those series (e.g. a ceiling-change month with no
 * obligation transaction). Activity data is also looked up by timestamp directly
 * rather than depending on the scatter series appearing in params.
 */
function makeSingleAwardFormatter(
  // oxlint-disable-next-line typescript/no-explicit-any
  activityByTime: Map<number, any>,
  oblData: [number, number][],
  ceilData: [number, number][],
  outData: [number, number][],
): (params: unknown) => string {
  const getObl = makeForwardFillLookup(oblData)
  const getCeil = ceilData.length > 0 ? makeForwardFillLookup(ceilData) : null
  const getOut = makeForwardFillLookup(outData)

  return (params: unknown): string => {
    const items = Array.isArray(params) ? params : [params]
    if (items.length === 0) return ''

    // oxlint-disable-next-line typescript/no-explicit-any
    const first = items[0] as any
    const t: number = first.value?.[0] ?? first.axisValue
    const date = new Date(t)
    const header = `${MONTH_ABBREVS[date.getMonth()]} ${date.getFullYear()}`

    const lineLines: string[] = []
    const ceilV = getCeil?.(t)
    if (ceilV != null) {
      lineLines.push(
        `${markerDot('#9ca3af')} Award Ceiling: <strong>${formatCurrency(ceilV)}</strong>`,
      )
    }
    const oblV = getObl(t)
    if (oblV != null) {
      lineLines.push(
        `${markerDot('#3b82f6')} Award Obligated: <strong>${formatCurrency(oblV)}</strong>`,
      )
    }
    const outV = getOut(t)
    if (outV != null) {
      lineLines.push(
        `${markerDot('#22c55e')} Award Disbursed: <strong>${formatCurrency(outV)}</strong>`,
      )
    }

    const activityData = activityByTime.get(t)
    let activityBlock = ''
    if (activityData) {
      const amount: number = activityData.txAmount ?? 0
      const count: number = activityData.txCount ?? 1
      const description: string | null = activityData.txDescription ?? null
      const isPos = amount >= 0
      const amtColor = isPos ? '#60a5fa' : '#f87171'
      const sign = isPos ? '+' : ''
      const descText =
        description && description.length > 200
          ? description.slice(0, 197) + '…'
          : description
      const moreCount = count - 1
      activityBlock =
        `<div style="border-top:1px solid rgba(148,163,184,0.2);margin-top:6px;padding-top:6px;` +
        `font-family:ui-monospace,'Cascadia Code',monospace">` +
        `<div style="font-size:9px;font-weight:700;letter-spacing:0.1em;color:#94a3b8;margin-bottom:4px;text-transform:uppercase">Activity</div>` +
        `<div style="display:flex;justify-content:space-between;gap:16px;font-size:10px">` +
        `<span style="color:#64748b">Net Obligation</span>` +
        `<span style="color:${amtColor};font-weight:700">${sign}${formatCurrency(amount)}</span>` +
        `</div>` +
        `<div style="display:flex;justify-content:space-between;gap:16px;font-size:10px;margin-top:1px">` +
        `<span style="color:#64748b">Modifications</span>` +
        `<span style="color:#cbd5e1;font-weight:600">${count}</span>` +
        `</div>` +
        (descText
          ? `<div style="margin-top:4px;font-size:10px;color:#94a3b8;word-break:break-word;white-space:normal;max-width:240px">${descText}` +
            (moreCount > 0
              ? `<span style="color:#475569;margin-left:4px">(${moreCount} more…)</span>`
              : '') +
            `</div>`
          : moreCount > 0
            ? `<div style="margin-top:4px;font-size:10px;color:#475569">(${count} modifications)</div>`
            : '') +
        `</div>`
    }

    const linesHtml =
      lineLines.length > 0 ? `<br/>${lineLines.join('<br/>')}` : ''
    return `<div style="font-size:12px"><strong>${header}</strong>${linesHtml}${activityBlock}</div>`
  }
}

/** Common tooltip formatter using formatCurrency */
function tooltipFormatter(params: unknown): string {
  const items = Array.isArray(params) ? params : [params]
  if (items.length === 0) return ''

  // oxlint-disable-next-line typescript/no-explicit-any
  const first = items[0] as any
  const date = new Date(first.value?.[0] ?? first.axisValue)
  const header = `${MONTH_ABBREVS[date.getMonth()]} ${date.getFullYear()}`

  const lines = items
    // oxlint-disable-next-line typescript/no-explicit-any
    .filter(
      (item: any) =>
        item.value?.[1] != null &&
        !String(item.seriesName ?? '').startsWith('_'),
    )
    // oxlint-disable-next-line typescript/no-explicit-any
    .map((item: any) => {
      const val = item.value[1] as number
      return `${item.marker} ${item.seriesName}: <strong>${formatCurrency(val)}</strong>`
    })

  return `<div style="font-size:12px"><strong>${header}</strong><br/>${lines.join('<br/>')}</div>`
}

/**
 * IDV group tooltip formatter.
 *
 * Like the shared formatter but annotates each series value with its
 * percentage of total obligation, which makes stacked child breakdowns
 * immediately readable without needing a calculator.
 *
 * % denominator = "Recipient Obligated" value when present (cumulative mode),
 * otherwise the sum of all non-disbursed series values (monthly bar mode).
 */
function idvTooltipFormatter(params: unknown): string {
  const items = Array.isArray(params) ? params : [params]
  if (items.length === 0) return ''

  // oxlint-disable-next-line typescript/no-explicit-any
  const all = items as any[]
  const date = new Date(all[0].value?.[0] ?? all[0].axisValue)
  const header = `${MONTH_ABBREVS[date.getMonth()]} ${date.getFullYear()}`

  // Determine obligation total for % calculation
  // oxlint-disable-next-line typescript/no-explicit-any
  const oblItem = all.find(
    (item: any) => item.seriesName === 'Recipient Obligated',
  )
  let totalObl: number = (oblItem?.value?.[1] as number | undefined) ?? 0

  if (totalObl === 0) {
    // Monthly mode: sum all child bars (exclude disbursed line and _ prefixed)
    totalObl = all
      // oxlint-disable-next-line typescript/no-explicit-any
      .filter(
        (item: any) =>
          !String(item.seriesName ?? '').startsWith('_') &&
          item.seriesName !== 'Recipient Disbursed' &&
          item.value?.[1] != null,
      )
      // oxlint-disable-next-line typescript/no-explicit-any
      .reduce((sum: number, item: any) => sum + (item.value[1] as number), 0)
  }

  // Visible items: exclude _ prefixed
  const visible = all.filter(
    // oxlint-disable-next-line typescript/no-explicit-any
    (item: any) =>
      item.value?.[1] != null && !String(item.seriesName ?? '').startsWith('_'),
  )

  // Display order: Recipient Obligated first, Remaining Ceiling last, others in between
  const sorted = [
    // oxlint-disable-next-line typescript/no-explicit-any
    ...visible.filter((item: any) => item.seriesName === 'Recipient Obligated'),
    ...visible.filter(
      // oxlint-disable-next-line typescript/no-explicit-any
      (item: any) =>
        item.seriesName !== 'Recipient Obligated' &&
        item.seriesName !== 'Remaining Ceiling',
    ),
    // oxlint-disable-next-line typescript/no-explicit-any
    ...visible.filter((item: any) => item.seriesName === 'Remaining Ceiling'),
  ]

  const lines = sorted
    // oxlint-disable-next-line typescript/no-explicit-any
    .map((item: any) => {
      const val = item.value[1] as number
      const name = item.seriesName as string
      // "Recipient Obligated" is the 100% reference — no % annotation
      // "Remaining Ceiling" shows % of ceiling utilised (context, not obligation share)
      const isPrimary = name === 'Recipient Obligated'
      const isHeadroom = name === 'Remaining Ceiling'
      let pct = ''
      if (!isPrimary && !isHeadroom && totalObl > 0) {
        pct = ` <span style="opacity:0.65">(${((val / totalObl) * 100).toFixed(1)}%)</span>`
      }
      return `${item.marker} ${name}: <strong>${formatCurrency(val)}</strong>${pct}`
    })

  return `<div style="font-size:12px"><strong>${header}</strong><br/>${lines.join('<br/>')}</div>`
}

interface BaseAxes {
  grid: {
    left: number
    right: number
    top: number
    bottom: number
    containLabel: boolean
  }
  xAxis: {
    type: 'time'
    min?: number
    max?: number
    axisLabel: { fontSize: number; hideOverlap: boolean }
    splitLine: { show: boolean }
  }
  yAxis: {
    type: 'value'
    axisLabel: { fontSize: number; formatter: (v: number) => string }
    splitLine: { lineStyle: { color: string } }
    max?: number
  }
}

/** Common axis and grid configuration */
function baseAxes(
  expanded: boolean,
  xRange?: { min: number; max: number },
): BaseAxes {
  return {
    grid: {
      left: 8,
      right: 16,
      top: expanded ? 28 : 24,
      bottom: expanded ? 72 : 30,
      containLabel: true,
    },
    xAxis: {
      type: 'time',
      min: xRange?.min,
      max: xRange?.max,
      axisLabel: { fontSize: 10, hideOverlap: true },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: 10,
        formatter: (v: number) => formatCurrency(v),
      },
      splitLine: {
        lineStyle: { color: 'rgba(148, 163, 184, 0.1)' },
      },
    },
  }
}

/**
 * Compute a y-axis max that prevents a ceiling line from dominating the scale.
 * Returns undefined when no clamping is needed (ceiling is within threshold).
 */
const CEILING_THRESHOLD = 1.5
function clampYMax(
  dataSeries: [number, number][][],
  ceilingMax: number,
): number | undefined {
  const maxNonCeiling = dataSeries
    .flat()
    .reduce((max, [, y]) => (y > max ? y : max), 0)
  return ceilingMax > maxNonCeiling * CEILING_THRESHOLD && maxNonCeiling > 0
    ? maxNonCeiling * 1.15
    : undefined
}

/**
 * Compute X-axis range from primary data only (obligation + outlay).
 * Ceiling's flat lines often span the full aggregation window and would
 * stretch the X-axis far beyond the period with real activity.
 */
function computeXRange(
  primaryArrays: [number, number][][],
): { min: number; max: number; defaultStartPct: number } | undefined {
  let tMin = Infinity
  let tMax = -Infinity
  let hasData = false
  for (const data of primaryArrays) {
    for (const [t] of data) {
      if (t < tMin) tMin = t
      if (t > tMax) tMax = t
      hasData = true
    }
  }
  if (!hasData) return undefined
  const PAD = 15 * 24 * 60 * 60 * 1000 // ~2 weeks
  // Default zoom: show last 3 years, let user scroll back for older data
  const THREE_YEARS = 3 * 365.25 * 24 * 60 * 60 * 1000
  const dataRange = tMax - tMin + 2 * PAD
  const defaultStartPct =
    dataRange > THREE_YEARS ? ((dataRange - THREE_YEARS) / dataRange) * 100 : 0
  return { min: tMin - PAD, max: tMax + PAD, defaultStartPct }
}

/** DATA Act marker as markLine data */
function dataActMarkLine(hasPreReportingData: boolean) {
  if (!hasPreReportingData) return []
  return [
    {
      xAxis: OUTLAY_REPORTING_START.getTime(),
      label: {
        formatter: 'DATA Act (FY2022)',
        fontSize: 10,
        fontWeight: 'bold',
        color: '#b45309',
        rotate: 90,
        position: 'insideEndBottom' as const,
        distance: 4,
      },
      lineStyle: { color: '#d97706', type: 'dashed', width: 2 },
    },
  ]
}

// ============================================================================
// Shared dataZoom builder
// ============================================================================

/** SVG path for slider handle with grip lines */
const HANDLE_ICON =
  'path://M-6,-18 L6,-18 L6,18 L-6,18 Z M-3,-8 L-3,8 M0,-8 L0,8 M3,-8 L3,8'

interface DataZoomConfig {
  expanded: boolean
  fyZoom?: { startValue: number; endValue: number } | null
  zoomStart?: number
  minValueSpan?: number
}

function buildDataZoom(config: DataZoomConfig): DataZoomComponentOption[] {
  const { expanded, fyZoom, zoomStart = 0, minValueSpan } = config

  const position = fyZoom ?? { start: zoomStart, end: 100 }

  const inside: DataZoomComponentOption = {
    type: 'inside',
    xAxisIndex: 0,
    ...position,
    ...(minValueSpan != null ? { minValueSpan } : {}),
    // Disable native scroll-to-zoom — it's too sensitive and janky.
    // EChartsCashFlow applies a custom throttled wheel handler instead.
    zoomOnMouseWheel: false,
    moveOnMouseWheel: false,
    preventDefaultMouseMove: false,
  }

  if (!expanded) return [inside]

  const slider: DataZoomComponentOption = {
    type: 'slider',
    xAxisIndex: 0,
    height: 28,
    bottom: 4,
    moveHandleSize: 10,
    borderColor: 'transparent',
    ...position,
    ...(minValueSpan != null ? { minValueSpan } : {}),
    handleIcon: HANDLE_ICON,
    handleSize: 46, // Centered on body, oversized to reach moveHandle above
    handleStyle: {
      color: '#94a3b8',
      borderColor: '#94a3b8',
      borderWidth: 1,
      shadowBlur: 2,
      shadowColor: 'rgba(0,0,0,0.1)',
      shadowOffsetY: 1,
    },
    fillerColor: 'rgba(59, 130, 246, 0.12)',
    dataBackground: {
      lineStyle: { color: 'rgba(148, 163, 184, 0.3)', width: 1 },
      areaStyle: { color: 'rgba(148, 163, 184, 0.08)' },
    },
    selectedDataBackground: {
      lineStyle: { color: 'rgba(59, 130, 246, 0.5)', width: 1 },
      areaStyle: { color: 'rgba(59, 130, 246, 0.12)' },
    },
    textStyle: { color: 'transparent' },
  }

  return [inside, slider]
}

// ============================================================================
// buildAggregateOption
// ============================================================================

export interface AggregateOptionParams {
  obligationCurve: CumulativePoint[]
  ceilingCurve: CumulativePoint[] | null
  outlayCurve: CumulativePoint[] | null
  hasPreReportingData: boolean
  expanded: boolean
  chartMode?: ChartMode
  estimatedOutlayPct?: number
  fiscalYear?: number | 'all'
}

export function buildAggregateOption(
  params: AggregateOptionParams,
): EChartsOption {
  const {
    obligationCurve,
    ceilingCurve,
    outlayCurve,
    hasPreReportingData,
    expanded,
    chartMode = 'cumulative',
    estimatedOutlayPct = 0,
    fiscalYear,
  } = params

  const isMonthly = chartMode === 'monthly'

  const oblData = toTimeData(obligationCurve)
  // Drop ceiling entirely in monthly mode — sparse first-seen deltas are noise
  const ceilData = !isMonthly && ceilingCurve ? toTimeData(ceilingCurve) : []
  const outData = outlayCurve ? toTimeData(outlayCurve) : []

  const aggCeilingMax =
    ceilData.length > 0
      ? ceilData.reduce((max, [, y]) => (y > max ? y : max), 0)
      : 0
  const aggYMax = clampYMax([oblData, outData], aggCeilingMax)

  // Split disbursement into estimated (pre-DATA Act, dashed) vs real (post, solid)
  // Use index-based slicing with one shared overlap point to bridge the gap
  const reportingCutoff = OUTLAY_REPORTING_START.getTime()
  const cutoffIdx = hasPreReportingData
    ? outData.findIndex(([t]) => t >= reportingCutoff)
    : -1
  const outEstimated =
    cutoffIdx > 0
      ? outData.slice(0, cutoffIdx + 1) // include first real point so lines connect
      : cutoffIdx === -1 && hasPreReportingData
        ? outData // all pre-reporting
        : []
  const outReal =
    cutoffIdx >= 0
      ? outData.slice(cutoffIdx)
      : hasPreReportingData
        ? [] // all pre-reporting, no real data
        : outData

  const xRange = computeXRange([oblData, outData])
  const zoomStart = xRange?.defaultStartPct ?? 0

  // FY boundary mark lines — filter out FY2022 when DATA Act marker replaces it
  const startDate = obligationCurve[0]?.x
  const endDate = obligationCurve[obligationCurve.length - 1]?.x
  const dataActLines = dataActMarkLine(hasPreReportingData)
  const fyLines =
    startDate && endDate ? buildFyMarkLines(startDate, endDate) : []
  // DATA Act marker sits at Oct 1 2021 (= FY2022 boundary) — remove the duplicate FY line
  const filteredFyLines = hasPreReportingData
    ? fyLines.filter((l) => l.xAxis !== OUTLAY_REPORTING_START.getTime())
    : fyLines
  const allMarkLineData = [...filteredFyLines, ...dataActLines]

  // oxlint-disable-next-line typescript/no-explicit-any
  const series: any[] = []

  // Ceiling (dashed gray) — cumulative mode only
  if (ceilData.length > 0) {
    series.push({
      name: 'Ceiling',
      type: 'line',
      data: ceilData,
      lineStyle: { color: '#6b7280', type: 'dashed', width: 2 },
      itemStyle: { color: '#6b7280' },
      symbol: 'none',
      smooth: 0.1,
      markLine:
        series.length === 0
          ? {
              silent: true,
              symbol: 'none',
              label: { fontSize: 10, color: 'rgba(148, 163, 184, 0.4)' },
              data: allMarkLineData,
            }
          : undefined,
    })
  }

  // Monthly overlap strategy: two bar series with per-item colors.
  // "Back" = max(obl, out) per point, colored by whichever is taller.
  // "Front" = min(obl, out), colored by whichever is shorter.
  // This keeps exactly 2 bar series so ECharts overlaps them cleanly.
  if (isMonthly) {
    series.push(
      {
        name: 'Obligated',
        type: 'bar',
        data: oblData,
        barMinWidth: 8,
        itemStyle: {
          color: 'rgba(59, 130, 246, 0.55)',
          borderRadius: [2, 2, 0, 0],
        },
        legendIcon: 'rect',
        markLine:
          series.length === 0
            ? {
                silent: true,
                symbol: 'none',
                label: { fontSize: 10, color: 'rgba(148, 163, 184, 0.4)' },
                data: allMarkLineData,
              }
            : undefined,
      },
      {
        name: 'Disbursed',
        type: 'bar',
        data: outData,
        barMinWidth: 8,
        itemStyle: {
          color: 'rgba(34, 197, 94, 0.8)',
          borderRadius: [2, 2, 0, 0],
        },
        legendIcon: 'rect',
      },
    )
  }

  // Obligated + Disbursed — cumulative mode lines
  if (!isMonthly) {
    series.push({
      name: 'Obligated',
      type: 'line',
      data: oblData,
      lineStyle: { color: '#3b82f6', width: 2 },
      itemStyle: { color: '#3b82f6' },
      areaStyle: { color: 'rgba(59, 130, 246, 0.1)' },
      legendIcon: 'rect',
      symbol: 'none',
      smooth: 0.1,
      markLine:
        series.length === 0
          ? {
              silent: true,
              symbol: 'none',
              label: { fontSize: 10, color: 'rgba(148, 163, 184, 0.4)' },
              data: allMarkLineData,
            }
          : undefined,
    })
  }

  // Disbursed — in cumulative mode, split into estimated (dashed) and real (solid) lines.
  if (!isMonthly) {
    if (outEstimated.length > 0) {
      // Highlight estimated region when >30% of total outlay is estimated
      const showEstimatedHighlight =
        hasPreReportingData && estimatedOutlayPct > 0.3
      series.push({
        name: 'Disbursed (est.)',
        type: 'line',
        data: outEstimated,
        lineStyle: { color: '#22c55e', width: 1.5, type: 'dashed' },
        itemStyle: { color: 'rgba(34, 197, 94, 0.5)' },
        symbol: 'none',
        smooth: 0.1,
        ...(showEstimatedHighlight
          ? {
              markArea: {
                silent: true,
                itemStyle: { color: 'rgba(245, 158, 11, 0.04)' },
                data: [
                  [{ xAxis: oblData[0]?.[0] }, { xAxis: reportingCutoff }],
                ],
                label: {
                  show: true,
                  position: 'insideTop',
                  formatter: `~${Math.round(estimatedOutlayPct * 100)}% of disbursement estimated`,
                  fontSize: 10,
                  color: 'rgba(245, 158, 11, 0.5)',
                },
              },
            }
          : {}),
      })
    }
    if (outReal.length > 0) {
      series.push({
        name: 'Disbursed',
        type: 'line',
        data: outReal,
        lineStyle: { color: '#22c55e', width: 2 },
        itemStyle: { color: '#22c55e' },
        symbol: 'none',
        smooth: 0.1,
      })
    }
  }

  const aggBase = baseAxes(expanded, xRange)
  return {
    ...aggBase,
    yAxis: {
      ...aggBase.yAxis,
      ...(aggYMax !== undefined ? { max: aggYMax } : {}),
    },
    tooltip: {
      trigger: 'axis',
      confine: true,
      axisPointer: { type: expanded ? 'cross' : 'line' },
      formatter: tooltipFormatter,
    },
    legend: {
      show: true,
      type: 'scroll',
      top: 4,
      right: 8,
      textStyle: { fontSize: 10 },
      itemWidth: 20,
      itemHeight: 6,
      data: series
        .map((s: { name: string }) => s.name)
        .filter((n: string) => !n.startsWith('_')),
    },
    dataZoom: buildDataZoom({
      expanded,
      fyZoom:
        typeof fiscalYear === 'number'
          ? {
              startValue: new Date(fiscalYear - 1, 9, 1).getTime(),
              endValue: new Date(fiscalYear, 8, 30).getTime(),
            }
          : null,
      zoomStart,
      // 6 months min span prevents zooming in so far that bars disappear
      minValueSpan: 6 * 30 * 24 * 60 * 60 * 1000,
    }),
    animation: false,
    series,
    aria: { decal: { show: false } },
  }
}

// ============================================================================
// buildSingleAwardOption
// ============================================================================

export interface SingleAwardOptionParams {
  award: AwardPortfolioItem
  timeSeries: TimeSeriesPoint[]
  chartMode: ChartMode
  expanded: boolean
}

export function buildSingleAwardOption(
  params: SingleAwardOptionParams,
): EChartsOption {
  const { award, timeSeries, chartMode, expanded } = params
  const isMonthly = chartMode === 'monthly'
  const curves = computePerAwardCumulative(timeSeries, award)

  const monthlyObl = isMonthly ? computeMonthlyObligation(timeSeries) : null
  const displayObl = monthlyObl ?? curves.obligation
  const awardRatio =
    safeNum(award.total_obligation) > 0
      ? safeNum(award.total_outlays) / safeNum(award.total_obligation)
      : 0
  const displayOutlay =
    isMonthly && monthlyObl
      ? computeMonthlyOutlay(monthlyObl, awardRatio)
      : curves.outlay

  const oblData = toTimeData(displayObl)
  const outData = toTimeData(displayOutlay)
  const ceilData =
    !isMonthly && curves.ceiling ? toTimeData(curves.ceiling) : []

  const singleCeilingMax =
    ceilData.length > 0
      ? ceilData.reduce((max, [, y]) => (y > max ? y : max), 0)
      : 0
  const yMax = clampYMax([oblData, outData], singleCeilingMax)

  // Scatter overlay: one dot per active month (= monthly obligation transactions)
  const sortedTs = [...timeSeries].sort(
    (a, b) => a.year - b.year || a.month - b.month,
  )
  // O(1) lookup for cumulative y-position at each month
  const oblByTime = new Map(displayObl.map((d) => [d.x.getTime(), d.y]))
  const activeTs = sortedTs.filter((p) => p.amount !== 0)
  // Adaptive max symbol size: power-law decay so dots stay visible at all densities.
  // 14px at n≤10, 11px at n=20, 7.3px at n=60, 6px floor at n≥100
  const maxSymSize = Math.max(
    6,
    Math.min(14, 14 * Math.pow(10 / Math.max(activeTs.length, 1), 0.35)),
  )
  // Scale each dot relative to the largest transaction in this award
  const maxLogAmt = Math.log(
    Math.max(...activeTs.map((p) => Math.abs(p.amount)), 1) + 1,
  )
  const scatterData = activeTs.map((p) => {
    const t = new Date(p.year, p.month - 1, 1).getTime()
    const oblY = oblByTime.get(t) ?? 0
    const relMag =
      maxLogAmt > 0 ? Math.log(Math.abs(p.amount) + 1) / maxLogAmt : 1
    return {
      value: [t, oblY],
      symbolSize: Math.max(3, maxSymSize * (0.5 + 0.5 * relMag)),
      itemStyle: { color: p.amount >= 0 ? '#3b82f6' : '#ef4444' },
      txAmount: p.amount,
      txCount: p.transactionCount,
      txDescription: p.description ?? null,
    }
  })

  // PoP markers
  // oxlint-disable-next-line typescript/no-explicit-any
  const markerData: any[] = []
  if (award.period_of_performance_start_date) {
    markerData.push({
      xAxis: new Date(award.period_of_performance_start_date).getTime(),
      label: {
        formatter: 'PoP Start',
        fontSize: 10,
        fontWeight: 'bold',
        position: 'insideStartTop',
      },
      lineStyle: { color: '#3b82f6', type: 'dashed', width: 2 },
    })
  }
  if (award.period_of_performance_current_end_date) {
    markerData.push({
      xAxis: new Date(award.period_of_performance_current_end_date).getTime(),
      label: {
        formatter: 'PoP End',
        fontSize: 10,
        fontWeight: 'bold',
        position: 'insideStartTop',
      },
      lineStyle: { color: '#f59e0b', type: 'dashed', width: 2 },
    })
  }

  // Auto-zoom to award's date range (excluding ceiling from X range)
  const allTimestamps = [
    ...oblData.map(([t]) => t),
    ...outData.map(([t]) => t),
    ...markerData.map(
      // oxlint-disable-next-line typescript/no-explicit-any
      (m: any) => m.xAxis as number,
    ),
  ]
  const PAD_MS = 30 * 24 * 60 * 60 * 1000
  let xMin: number | undefined
  let xMax: number | undefined
  if (allTimestamps.length > 0) {
    const tMin = Math.min(...allTimestamps)
    const tMax = Math.max(...allTimestamps)
    const span = tMax - tMin
    const minSpan = 2 * PAD_MS
    const pad = span < minSpan ? (minSpan - span) / 2 + PAD_MS : PAD_MS
    xMin = tMin - pad
    xMax = tMax + pad
  }

  // oxlint-disable-next-line typescript/no-explicit-any
  const series: any[] = []

  if (ceilData.length > 0) {
    series.push({
      name: 'Award Ceiling',
      type: 'line',
      data: ceilData,
      lineStyle: { color: '#9ca3af', type: 'dashed', width: 2 },
      itemStyle: { color: '#9ca3af' },
      symbol: 'none',
      smooth: 0.1,
    })
  }

  series.push({
    name: 'Award Obligated',
    type: 'line',
    data: oblData,
    lineStyle: { color: '#3b82f6', width: 2.5 },
    itemStyle: { color: '#3b82f6' },
    legendIcon: 'rect',
    symbol: 'none',
    smooth: 0.1,
    markLine:
      markerData.length > 0
        ? { silent: true, symbol: 'none', data: markerData }
        : undefined,
  })

  series.push({
    name: 'Award Disbursed',
    type: 'line',
    data: outData,
    lineStyle: { color: '#22c55e', width: 2 },
    itemStyle: { color: '#22c55e' },
    legendIcon: 'rect',
    symbol: 'none',
    smooth: 0.1,
  })

  // Always push the Activity series — even with empty data — so ECharts never
  // destroys and recreates it between award switches. Recreating mid-hover
  // leaves a stale series reference in the axis-tooltip dispatch, causing a
  // getRawIndex crash in TooltipView._showAxisTooltip.
  series.push({
    name: 'Activity',
    type: 'scatter',
    data: scatterData,
    symbol: 'circle',
    cursor: 'pointer',
    z: 10,
  })

  // Map from timestamp → scatter point data.
  // The tooltip formatter looks up activity by the crosshair's x-timestamp directly,
  // so it fires correctly even when the axis snaps to a nearby line-series point
  // instead of the exact scatter dot timestamp.
  const activityByTime = new Map(
    scatterData.map((d) => [d.value[0] as number, d]),
  )

  // Legend items — explicit list keeps internal series (_-prefixed) out of the legend.
  const legendData: string[] = []
  if (ceilData.length > 0) legendData.push('Award Ceiling')
  legendData.push('Award Obligated', 'Award Disbursed', 'Activity')

  const base = baseAxes(expanded)
  return {
    ...base,
    xAxis: {
      type: 'time',
      min: xMin,
      max: xMax,
      axisLabel: { fontSize: 10, hideOverlap: true },
      splitLine: { show: false },
    },
    yAxis: {
      ...base.yAxis,
      ...(yMax !== undefined ? { max: yMax } : {}),
    },
    tooltip: {
      trigger: 'axis',
      confine: true,
      axisPointer: { type: 'cross' },
      formatter: makeSingleAwardFormatter(
        activityByTime,
        oblData,
        ceilData,
        outData,
      ),
    },
    legend: {
      show: true,
      type: 'scroll',
      top: 4,
      right: 8,
      textStyle: { fontSize: 10 },
      itemWidth: 20,
      itemHeight: 6,
      data: legendData,
    },
    dataZoom: buildDataZoom({ expanded }),
    animation: false,
    series,
    aria: { decal: { show: false } },
  }
}

// ============================================================================
// buildIdvGroupOption
// ============================================================================

export interface IdvGroupOptionParams {
  group: IdvGroup
  obligationCurve: CumulativePoint[]
  chartMode: ChartMode
  idvGroupTimeSeries: IdvGroupTimeSeriesResponse | null
  expanded: boolean
}

export function buildIdvGroupOption(
  params: IdvGroupOptionParams,
): EChartsOption {
  const { group, chartMode, idvGroupTimeSeries, expanded } = params
  const isMonthly = chartMode === 'monthly'

  // oxlint-disable-next-line typescript/no-explicit-any
  const series: any[] = []
  // Used for X-axis range computation
  const xRangeArrays: [number, number][][] = []
  // Explicit legend items — excludes '_' prefixed internal series
  const legendItems: string[] = []

  // Markers: ordering period end + PoP end
  // oxlint-disable-next-line typescript/no-explicit-any
  const markerData: any[] = []
  const orderingEnd = group.parentAward?.ordering_period_end_date
  if (orderingEnd) {
    markerData.push({
      xAxis: new Date(orderingEnd).getTime(),
      label: {
        formatter: 'Ordering Ends',
        fontSize: 10,
        fontWeight: 'bold',
        position: 'insideStartTop',
      },
      lineStyle: { color: '#ef4444', type: 'dashed', width: 2 },
    })
  }
  const popEnd = group.parentAward?.period_of_performance_current_end_date
  if (popEnd) {
    markerData.push({
      xAxis: new Date(popEnd).getTime(),
      label: {
        formatter: 'PoP End',
        fontSize: 10,
        fontWeight: 'bold',
        position: 'insideStartTop',
      },
      lineStyle: { color: '#f59e0b', type: 'dashed', width: 2 },
    })
  }

  if (idvGroupTimeSeries && idvGroupTimeSeries.combined.length > 0) {
    // Seed cumulative curve with obligation that predates the ClickHouse time series window.
    // Without this, the chart shows near-$0 values when most of the obligation came from
    // older transactions that fall outside the table's retention window.
    const tsOblTotal = idvGroupTimeSeries.combined.reduce(
      (sum, p) => sum + p.amount,
      0,
    )
    const preRange = Math.max(0, group.recipientObligation - tsOblTotal)
    const rawOblCumulative = computeCumulativeObligation(
      idvGroupTimeSeries.combined,
      preRange,
    )

    // ECharts line/area series with symbol:none are invisible with only 1 data point —
    // there's no second point to draw the step toward. When pre-range seeding is active
    // (most obligation predates the ClickHouse retention window), the TS may contain
    // very few points. Prepend an anchor at `preRange` so ECharts can draw a visible
    // horizontal step from the award's start date to the first real TS point.
    let combinedOblCumulative = rawOblCumulative
    if (preRange > 0 && rawOblCumulative.length > 0) {
      const firstDate = rawOblCumulative[0].x
      // Prefer real award dates so the X axis reflects genuine history.
      const parentSigned = group.parentAward?.date_signed
      const earliestChildPoP = group.children
        .map((c) => c.period_of_performance_start_date)
        .filter(Boolean)
        .sort()[0]
      const signStr = parentSigned ?? earliestChildPoP
      const candidateDate = signStr ? new Date(signStr) : null
      const anchorDate =
        candidateDate && candidateDate < firstDate
          ? candidateDate
          : new Date(firstDate.getFullYear(), firstDate.getMonth() - 12, 1)
      combinedOblCumulative = [
        { x: anchorDate, y: preRange },
        ...rawOblCumulative,
      ]
    }

    // Shared dense time axis — used to forward-fill sparse child curves
    const timeAxis = combinedOblCumulative.map((p) => p.x)

    const LAG = 3
    const groupOutlayRatio =
      group.recipientObligation > 0
        ? group.recipientOutlay / group.recipientObligation
        : 0

    const combinedObl = toTimeData(combinedOblCumulative)

    if (isMonthly) {
      // D — Monthly mode: stacked bars per child TO + disbursement line overlay
      const monthlyOblArray = computeMonthlyObligation(
        idvGroupTimeSeries.combined,
      )
      const monthlyTimeAxis = monthlyOblArray.map((p) => p.x)
      const childrenToPlot = idvGroupTimeSeries.children

      if (childrenToPlot.length > 0) {
        childrenToPlot.forEach((child, i) => {
          const childMonthly = computeMonthlyObligation(child.points)
          const childByTime = new Map(
            childMonthly.map((p) => [p.x.getTime(), p.y]),
          )
          const childData: [number, number][] = monthlyTimeAxis.map((d) => [
            d.getTime(),
            childByTime.get(d.getTime()) ?? 0,
          ])

          const groupChild = group.children.find(
            (c) => c.generated_unique_award_id === child.awardId,
          )
          const label = groupChild?.display_award_id ?? `TO ${i + 1}`

          xRangeArrays.push(childData)
          legendItems.push(label)
          series.push({
            name: label,
            type: 'bar',
            data: childData,
            stack: 'children',
            barMaxWidth: 18,
            barMinWidth: 8,
            itemStyle: {
              color: getChildColor(i),
            },
          })
        })
      } else {
        // Fallback: single combined bar when no child breakdowns
        const monthlyObl = toTimeData(monthlyOblArray)
        xRangeArrays.push(monthlyObl)
        legendItems.push('Recipient Obligated')
        series.push({
          name: 'Recipient Obligated',
          type: 'bar',
          data: monthlyObl,
          barMaxWidth: 18,
          barMinWidth: 8,
          itemStyle: {
            color: 'rgba(59, 130, 246, 0.7)',
            borderRadius: [2, 2, 0, 0],
          },
        })
      }

      // Disbursement line overlay (thin green).
      // Use monthlyOblArray (raw TS amounts) rather than decumulate(combinedOblCumulative).
      // combinedOblCumulative may have a synthetic anchor point prepended, whose massive
      // first delta would corrupt the monthly series and stretch the X-axis into the past.
      const combinedOutMonthly = toTimeData(
        computeMonthlyOutlay(monthlyOblArray, groupOutlayRatio),
      )
      xRangeArrays.push(combinedOutMonthly)
      legendItems.push('Recipient Disbursed')
      series.push({
        name: 'Recipient Disbursed',
        type: 'line',
        data: combinedOutMonthly,
        lineStyle: { color: '#22c55e', width: 2 },
        itemStyle: { color: '#22c55e' },
        symbol: 'circle',
        symbolSize: 3,
        smooth: 0.3,
      })
    } else {
      // Cumulative mode

      const combinedOutCumulative = combinedOblCumulative.map((p, i) => ({
        x: p.x,
        y: combinedOblCumulative[Math.max(0, i - LAG)].y * groupOutlayRatio,
      }))
      const combinedOut = toTimeData(combinedOutCumulative)

      xRangeArrays.push(combinedObl, combinedOut)

      // C — Disbursement gap band (drawn first, lowest z-order)
      // Green fill from 0 → outlay; amber fill from outlay → obligation
      legendItems.push('Recipient Disbursed')
      series.push({
        name: 'Recipient Disbursed',
        type: 'line',
        data: combinedOut,
        lineStyle: { color: '#22c55e', width: 1.5 },
        itemStyle: { color: '#22c55e' },
        areaStyle: { color: 'rgba(34, 197, 94, 0.3)' },
        legendIcon: 'rect',
        symbol: 'none',
        step: 'start',
        stack: 'gap',
        silent: false,
        z: 2,
      })
      const gapData: [number, number][] = combinedObl.map(([t, oblY], i) => [
        t,
        Math.max(0, oblY - (combinedOut[i]?.[1] ?? 0)),
      ])
      series.push({
        name: '_GapFill',
        type: 'line',
        data: gapData,
        lineStyle: { width: 0 },
        itemStyle: { color: 'rgba(245, 158, 11, 0.1)' },
        areaStyle: { color: 'rgba(245, 158, 11, 0.08)' },
        symbol: 'none',
        step: 'start',
        stack: 'gap',
        silent: true,
        z: 2,
      })

      // B — Stacked child TO areas (colored fills with visible edge lines for accessibility).
      // Only rendered when there are 2+ children — for a single child the area is identical
      // to the "Recipient Obligated" line and completely obscures the disbursement fills,
      // making the chart uninformative. With 1 child the disbursement gap fills dominate.
      const childrenToPlot = idvGroupTimeSeries.children
      if (childrenToPlot.length > 1) {
        childrenToPlot.forEach((child, i) => {
          // Seed each child with obligation that predates the ClickHouse retention
          // window, mirroring the combined curve's preRange logic. Without this,
          // children of long-running IDVs appear invisible at the combined scale.
          const groupChild = group.children.find(
            (c) => c.generated_unique_award_id === child.awardId,
          )
          const childTotal = safeNum(groupChild?.total_obligation)
          const childTsSum = child.points.reduce((sum, p) => sum + p.amount, 0)
          const childPreRange = Math.max(0, childTotal - childTsSum)

          const childCurve = computeCumulativeObligation(
            child.points,
            childPreRange,
          )
          // If pre-range is active, prepend an anchor at the combined curve's
          // start so forward-fill holds the seed value until the first real point.
          const anchoredChildCurve =
            childPreRange > 0 && childCurve.length > 0 && timeAxis.length > 0
              ? [{ x: timeAxis[0], y: childPreRange }, ...childCurve]
              : childCurve
          const childFilled = forwardFillCurve(anchoredChildCurve, timeAxis)
          const childData = toTimeData(childFilled)

          const label = groupChild?.display_award_id ?? `TO ${i + 1}`
          const color = getChildColor(i)

          legendItems.push(label)
          series.push({
            name: label,
            type: 'line',
            // Visible top-edge line per segment — provides a non-color cue for
            // colorblind users and creates clear visual separation between TOs.
            lineStyle: { color, width: 1.5 },
            itemStyle: { color },
            areaStyle: { color, opacity: 0.55 },
            legendIcon: 'rect',
            data: childData,
            symbol: 'none',
            step: 'start',
            stack: 'children',
            cursor: 'pointer',
            z: 3,
          })
        })
      }

      // "Recipient Obligated" line — NOT stacked, floats on top as reference
      legendItems.push('Recipient Obligated')
      series.push({
        name: 'Recipient Obligated',
        type: 'line',
        data: combinedObl,
        lineStyle: { color: '#3b82f6', width: 2.5 },
        itemStyle: { color: '#3b82f6' },
        legendIcon: 'rect',
        symbol: 'none',
        step: 'start',
        z: 10,
      })

      // Headroom — invisible on the chart, surfaces only in the hover tooltip.
      // Gives users ceiling context without cluttering the visual.
      // Not added to legendItems so it stays out of the legend.
      if (group.recipientCeiling > 0) {
        const headroomData: [number, number][] = combinedObl.map(
          ([t, oblY]) => [t, Math.max(0, group.recipientCeiling - oblY)],
        )
        series.push({
          name: 'Remaining Ceiling',
          type: 'line',
          data: headroomData,
          lineStyle: { width: 0 },
          itemStyle: { color: 'rgba(245, 158, 11, 0.75)' },
          areaStyle: { opacity: 0 },
          symbol: 'none',
          step: 'start',
          z: 1,
        })
      }
    }
  }

  // Attach markLine to first series if available
  if (markerData.length > 0 && series.length > 0) {
    series[0].markLine = {
      silent: true,
      symbol: 'none',
      data: markerData,
    }
  }

  const idvYMax = clampYMax(xRangeArrays, group.recipientCeiling)

  const idvBase = baseAxes(expanded, computeXRange(xRangeArrays))
  return {
    ...idvBase,
    yAxis: {
      ...idvBase.yAxis,
      ...(idvYMax !== undefined ? { max: idvYMax } : {}),
    },
    tooltip: {
      trigger: 'axis',
      confine: true,
      axisPointer: { type: 'cross' },
      formatter: idvTooltipFormatter,
    },
    legend: {
      show: true,
      type: 'scroll',
      top: 4,
      right: 8,
      textStyle: { fontSize: 10 },
      itemWidth: 20,
      itemHeight: 6,
      data: legendItems,
    },
    dataZoom: buildDataZoom({ expanded }),
    animation: false,
    series,
    aria: { decal: { show: false } },
  }
}

// ============================================================================
// buildCategoryBreakdownOption
// ============================================================================

export interface CategoryBreakdownOptionParams {
  contractCurve: CumulativePoint[]
  grantCurve: CumulativePoint[]
  idvCurve: CumulativePoint[]
  expanded: boolean
  chartMode?: ChartMode
}

export function buildCategoryBreakdownOption(
  params: CategoryBreakdownOptionParams,
): EChartsOption {
  const {
    contractCurve,
    grantCurve,
    idvCurve,
    expanded,
    chartMode = 'cumulative',
  } = params
  const isMonthly = chartMode === 'monthly'

  const contractData = toTimeData(contractCurve)
  const grantData = toTimeData(grantCurve)
  const idvData = toTimeData(idvCurve)

  // oxlint-disable-next-line typescript/no-explicit-any
  const series: any[] = []

  const CONTRACT_COLOR = '#3b82f6'
  const GRANT_COLOR = '#22c55e'
  const IDV_COLOR = '#f59e0b'

  if (isMonthly) {
    // Overlapping bar chart for monthly category breakdown.
    // Each category renders independently (no stack) with barGap: '-100%'
    // so all three are visible even when magnitudes differ wildly.
    if (contractData.length > 0) {
      series.push({
        name: 'Contracts',
        type: 'bar',
        data: contractData,
        barMinWidth: 20,
        itemStyle: { color: CONTRACT_COLOR, opacity: 0.65 },
        legendIcon: 'rect',
        emphasis: { focus: 'series' },
        barGap: '-100%',
      })
    }
    if (grantData.length > 0) {
      series.push({
        name: 'Grants',
        type: 'bar',
        data: grantData,
        barMinWidth: 20,
        itemStyle: { color: GRANT_COLOR, opacity: 0.65 },
        legendIcon: 'rect',
        emphasis: { focus: 'series' },
        barGap: '-100%',
      })
    }
    if (idvData.length > 0) {
      series.push({
        name: 'IDVs',
        type: 'bar',
        data: idvData,
        barMinWidth: 20,
        itemStyle: { color: IDV_COLOR, opacity: 0.65 },
        legendIcon: 'rect',
        emphasis: { focus: 'series' },
        barGap: '-100%',
      })
    }
  } else {
    // Cumulative area lines
    if (contractData.length > 0) {
      series.push({
        name: 'Contracts',
        type: 'line',
        data: contractData,
        lineStyle: { color: CONTRACT_COLOR, width: 2 },
        itemStyle: { color: CONTRACT_COLOR },
        areaStyle: { color: 'rgba(59, 130, 246, 0.08)' },
        legendIcon: 'rect',
        symbol: 'none',
        smooth: 0.1,
        emphasis: { focus: 'series' },
      })
    }

    if (grantData.length > 0) {
      series.push({
        name: 'Grants',
        type: 'line',
        data: grantData,
        lineStyle: { color: GRANT_COLOR, width: 2 },
        itemStyle: { color: GRANT_COLOR },
        areaStyle: { color: 'rgba(34, 197, 94, 0.08)' },
        legendIcon: 'rect',
        symbol: 'none',
        smooth: 0.1,
        emphasis: { focus: 'series' },
      })
    }

    if (idvData.length > 0) {
      series.push({
        name: 'IDVs',
        type: 'line',
        data: idvData,
        lineStyle: { color: IDV_COLOR, width: 2 },
        itemStyle: { color: IDV_COLOR },
        areaStyle: { color: 'rgba(245, 158, 11, 0.08)' },
        legendIcon: 'rect',
        symbol: 'none',
        smooth: 0.1,
        emphasis: { focus: 'series' },
      })
    }
  }

  return {
    ...baseAxes(expanded),
    tooltip: {
      trigger: 'axis',
      confine: true,
      axisPointer: { type: 'cross' },
      formatter: tooltipFormatter,
    },
    legend: {
      show: true,
      type: 'scroll',
      top: 4,
      right: 8,
      textStyle: { fontSize: 10 },
      itemWidth: 20,
      itemHeight: 6,
    },
    dataZoom: buildDataZoom({ expanded }),
    animation: false,
    series,
    aria: { decal: { show: false } },
  }
}
